import { AUTO_LOOKUP_BLOCK, INSURANCE_FIND_BLOCKS } from '../constants';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { CustomersController } from '@/shared/endpoints/customers';
import { Box, Button } from '@/shared/ui';
import { useTheme } from '@mui/material/styles';
import { selectInsuranceLookupTriggered, selectOnboardingUser } from '@/store/slices/onboarding/selectors';
import { setInsuranceLookupTriggered } from '@/store/slices/onboarding';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { GtmEvent } from '@/shared/types/enums';
import { useEffect, useState } from 'react';
import { setPageType } from '@/shared/helpers/page-type';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/shared/constants/routes';
import { checkEligibilityAndSave } from '@/shared/helpers/eligibility';
import { ICustomersInsuranceManualResponse } from '@/shared/endpoints/customers';
import { setEligibilityFailedModalOpen } from '@/store/slices/onboarding';
import { OutletContextType } from '../outletWrapper';
import { useOutletContext } from "react-router-dom";

interface IProps {
    onLookup(): void;
    onGoBack(): void;
}

const InsuranceEmptyState = ({ onLookup, onGoBack }: IProps) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const sendDataToGTM = useGTMDispatch();
    const navigate = useNavigate();
    const user = useAppSelector(selectOnboardingUser);
    const lookupTriggered = useAppSelector(selectInsuranceLookupTriggered);
    const showLookup = !lookupTriggered && !user?.userProfile?.lastEligibilityRun;
    const [isLoading, setIsLoading] = useState(false);
    const [insuranceLookupYieldedNothing, setInsuranceLookupYieldedNothing] = useState(false);
    const { onPayWithCash } = useOutletContext<OutletContextType>();

    const lookUp = async () => {
        setIsLoading(true);
        await dispatch(CustomersController.eligibility()).then(async (res) => {
            dispatch(CustomersController.get());
            const count = ((res?.payload || []) as unknown[]).length;
            sendDataToGTM({
                event: GtmEvent.INSURANCE_LOOKUP_COMPLETED,
                userId: user?.userProfile?.accountId,
                count
            });
            if (count === 0) {
                setInsuranceLookupYieldedNothing(true);
            }
        });
        dispatch(setInsuranceLookupTriggered(true));
        onLookup();
        setIsLoading(false);
    };

    const _conv_page_type = showLookup ? 'show_lookup' : 'choose_type_of_adding_insurance';

    const handleNavigate = (path: keyof typeof ROUTES.ONBOARDING.INSURANCE) => {
        navigate(`/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.INSURANCE.MAIN}/${ROUTES.ONBOARDING.INSURANCE[path]}`);
    };

    useEffect(() => {
        setPageType(_conv_page_type);
    }, [_conv_page_type]);

    return (
        <div
            className="insurance-step-container empty-state flex w-full flex-col flex-wrap items-center"
            data-testid="insurance-empty-state"
        >
            {insuranceLookupYieldedNothing ? (
                <div data-testid="lookup-view" className="insurance-step-container empty-state flex w-full flex-col flex-wrap items-center">
                    <h2 className="mb-[28.8px] text-center font-agrandir text-[2.8125rem] font-normal sm-max:text-2xl">
                        Looks like we couldn’t locate your
                        <br /> insurance details.
                    </h2>

                    <h3 className="px-[28px] text-center font-agrandir text-[32px] sm-max:text-base">
                        Please select an alternative way to add your insurance
                    </h3>
                </div>
            ) : (
                <>
                    <h2
                        data-testid="easly-view"
                        className="insurance__heading text-center font-agrandir text-[2.8125rem] font-normal sm-max:text-2xl"
                    >
                        <span className="text-primary">Easily </span>
                        Add Your Insurance!
                    </h2>

                    {showLookup && (
                        <Box
                            data-testid="insurance-finder"
                            bgWhite
                            shadow
                            sx={{
                                borderRadius: '1.1875rem',
                                width: 546,
                                maxWidth: '100%',
                                marginTop: '28.8px',
                                [theme.breakpoints.up('lg')]: { padding: '1.875rem 2.1875rem' }
                            }}
                        >
                            <div className="flex w-full flex-col items-center" data-testid="insurance-option-container">
                                {AUTO_LOOKUP_BLOCK.icon}
                                <h3 className="mt-3 text-center font-agrandir text-21 text-2xl font-semibold sm-max:text-xl">
                                    {AUTO_LOOKUP_BLOCK.name}
                                </h3>
                                <p className="mt-3 text-center text-base">{AUTO_LOOKUP_BLOCK.description}</p>

                                <Button
                                    className="insurance__lookup_btn"
                                    sx={{
                                        width: '100%',
                                        marginTop: '24px'
                                    }}
                                    showEndIcon
                                    onClick={lookUp}
                                    loading={isLoading}
                                    testId="button-insurance-empty-state-lookup"
                                    fontClassName='font-bold'
                                >
                                    Look Up My Insurance
                                </Button>
                            </div>
                        </Box>
                    )}

                    <h3 className="mt-[44.8px] px-[28px] text-center font-agrandir text-2xl sm-max:mt-[32px] sm-max:text-21">
                        Want to add your insurance in a different way?
                    </h3>
                </>
            )}

            <div className="mt-[32px] flex max-w-[100%] lg-max:flex-col">
                {INSURANCE_FIND_BLOCKS.map(way => (
                    <Box
                        key={way.type}
                        bgWhite
                        shadow
                        sx={{
                            borderRadius: '1.1875rem',
                            width: 378,
                            maxWidth: '100%',
                            [theme.breakpoints.up('lg')]: {
                                padding: '1.875rem 2.1875rem',
                                '&:not(:last-child)': { marginRight: '28.8px' }
                            },
                            [theme.breakpoints.down('lg')]: {
                                padding: '1.25rem 1.625rem',
                                '&:not(:last-child)': { marginBottom: '24px' }
                            }
                        }}
                    >
                        <div className="flex w-full flex-col items-center" key={way.type}>
                            {way.icon}
                            <h3 className="font-agrandir text-xl font-semibold text-center">{way.name}</h3>
                            <p className="mt-3 text-center text-base text-center">{way.description}</p>
                            {/* @ts-ignore-next-line */}
                            <Button
                                disabled={isLoading}
                                className={`insurance__add_${way.type}_btn`}
                                theme="dark"
                                variant="outlined"
                                sx={{ width: '100%', marginTop: '24px' }}
                                onClick={() => handleNavigate(way.path as keyof typeof ROUTES.ONBOARDING.INSURANCE)}
                                testId={`button-insurance-empty-state-option-${way.type}`}
                                fontClassName='font-bold'
                            >
                                {way.buttonName}
                            </Button>
                        </div>
                    </Box>
                ))}
            </div>
            <div className='w-full flex flex-col items-center gap-[1.25rem] mt-[2.5rem] sm:mt-[2.875rem]'>
                <span className=" font-agrandir text-27 leading-[130%] text-center">Do not have insurance?</span>
                <div className='flex flex-col items-center justify-center max-w-[438px] w-full gap-[20px]'>
                    <Button
                        disabled={isLoading}
                        type="button"
                        sx={{ width: '100%' }}
                        fontClassName='font-dmSansMedium font-medium text-18 leading-[130%] py-[5px]'
                        variant="outlined"
                        onClick={onPayWithCash}
                        testId="button-insurance-empty-state-paying-cash"
                    >
                        I am paying cash
                    </Button>
                    <Button
                        fontClassName="font-dmSansMedium font-bold text-17 leading-[130%] tracking-[.0213rem]"
                        testId="button-insurance-list-back"
                        variant="text"
                        className="button-insurance-list-back w-fit"
                        onClick={onGoBack}
                    >
                        {`< Back`}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default withScrollRestoration(InsuranceEmptyState);
